/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import { or } from '@ember/object/computed';
import { task } from 'ember-concurrency';
import EventBusService, { VWB_ACTION_UPDATED } from 'cio/services/event-bus';
import { VWBTab } from 'cio/utils/visual-workflow-builder/types';
import ApiErrorsService from 'cio/services/api-errors';
import VisualWorkflowBuilderService from 'cio/services/visual-workflow-builder';
import ActionModel from 'cio/models/action';
import { tracked } from '@glimmer/tracking';

interface EditActionSidebarSplitArgs {
  actionAbstract: any;
  isAddedOrMoving: boolean;
  tab: VWBTab;
  onSelect: (action: ActionModel) => unknown;
  onDeselect: () => unknown;
  onDeleteStarted: () => unknown;
  onDeleteConfirmed: () => unknown;
  onSetupComplete: () => unknown;
  hasReadOnlyPermission: boolean;
}

export default class Split extends Component<EditActionSidebarSplitArgs> {
  @service apiErrors!: ApiErrorsService;
  @service eventBus!: EventBusService;
  @service visualWorkflowBuilder!: VisualWorkflowBuilderService;

  @or('saveSplit.isRunning', 'deleteSplit.isRunning', 'pauseTest.isRunning', 'resumeTest.isRunning') isSaving!: boolean;

  @tracked selectedWinner: ActionModel | null = null;

  saveSplit = task({ drop: true }, async () => {
    try {
      let { actionAbstract } = this.args;
      let promise = actionAbstract.saveTask.perform();
      this.visualWorkflowBuilder.redrawConnectingLines();
      await promise;
      this.eventBus.publish(VWB_ACTION_UPDATED, actionAbstract.action);
      this.args.onDeselect();
      this.visualWorkflowBuilder.redrawConnectingLines();
    } catch (e) {
      this.apiErrors.report(e);
      throw e;
    }
  });

  deleteSplit = task({ drop: true }, async () => {
    this.args.onDeleteStarted();
    let childActions = this.args.actionAbstract.action.splitActions ?? [];

    if (childActions.length < 1) {
      return;
    }
    let [variation] = childActions;
    this.selectedWinner = variation;
  });

  pauseTest = task({ drop: true }, async () => {
    let { action } = this.args.actionAbstract;
    let promise = action.pauseTest();

    this.apiErrors.attach(promise);

    try {
      await promise;
    } catch (e) {
      action.rollbackAttributes();
      throw e;
    }
  });

  resumeTest = task({ drop: true }, async () => {
    let { action } = this.args.actionAbstract;
    let promise = action.startTest();

    this.apiErrors.attach(promise);

    try {
      await promise;
    } catch (e) {
      action.rollbackAttributes();
      throw e;
    }
  });

  @action
  onWinnerSelected() {
    this.args.actionAbstract.action.belongsTo('campaign').value()?.parseTree();
    this.selectedWinner = null;
    this.args.onDeselect();
    this.visualWorkflowBuilder.redrawConnectingLines();
  }
}
